import { sanitizeErrorParams, type ErrorParams } from '@financialharmony/ui/errors';
import { errorSentence } from '@/i18n/errorSentence';

/**
 * Extracts the actual error response from a Supabase FunctionsHttpError
 * @param error The error to parse
 * @returns The parsed error object or null if not a FunctionsHttpError
 */
export async function extractFunctionError(error: any): Promise<any | null> {
  if (error && error.name === 'FunctionsHttpError' && error.context) {
    try {
      return await error.context.json();
    } catch {
      return null;
    }
  }
  return null;
}
/** FIN-968: the bank transaction already settles another document (backend errorType). */
export const TRANSACTION_ALREADY_SETTLED = "TRANSACTION_ALREADY_SETTLED";
/** FIN-1023 D7/AC-13: a settlement whose own-currency magnitude doesn't match the document's
 * remaining foreign amount (a partial payment, overpayment, or fee-deducted underpayment —
 * backend errorType). */
export const SETTLEMENT_AMOUNT_MISMATCH = "SETTLEMENT_AMOUNT_MISMATCH";

/** Every named, permanent match-confirmation refusal this flow can produce, mapped to its
 * toast title — never "please try again" for one of these (FIN-968, FIN-1023 AC-13). */
const NAMED_MATCH_REFUSALS: Record<string, string> = {
  [TRANSACTION_ALREADY_SETTLED]: "Transaction already matched",
  [SETTLEMENT_AMOUNT_MISMATCH]: "Partial settlement",
};

/** The toast title for a named match-confirmation refusal (keyed off the CodedError's own
 * `.error` code set by settledRefusalOrNull), else null. */
export function matchRefusalTitle(error: unknown): string | null {
  const coded = error as CodedError | null;
  return coded?.error ? NAMED_MATCH_REFUSALS[coded.error] ?? null : null;
}

/**
 * An Error carrying the backend's structured error code, which callers branch on instead of
 * matching message text (FIN-381).
 */
export interface CodedError extends Error {
  error: string;
  details?: unknown;
  /** The variable parts of the code's catalog sentence, when the server sent any. */
  errorParams?: ErrorParams;
}

/**
 * Builds a CodedError. Every edge-function client had this same three-line shape inline, each
 * needing its own `as any` casts to attach the two fields — this keeps the shape in one typed
 * place instead.
 */
export function codedError(code: string, details?: unknown): CodedError {
  const err = new Error(code) as CodedError;
  err.error = code;
  err.details = details;
  return err;
}

/**
 * FIN-968 (widened by FIN-1023 D7/AC-13): reads a failed edge-function call's body once.
 * `settled` is a CodedError carrying the server's message when the body names ANY of
 * NAMED_MATCH_REFUSALS (TRANSACTION_ALREADY_SETTLED or SETTLEMENT_AMOUNT_MISMATCH) — the name
 * "settled" predates the second code but is kept to avoid a repo-wide rename across every
 * existing caller; `body` is the parsed body either way, for handleError's preParsedBody (a
 * Response body can only be read once).
 */
export async function settledRefusalOrNull(
  error: unknown,
): Promise<{ settled: CodedError | null; body: { error?: string; message?: string } | null }> {
  const body: { error?: string; message?: string } | null = await extractFunctionError(error);
  if (!body?.error || !(body.error in NAMED_MATCH_REFUSALS)) return { settled: null, body };
  const settled = codedError(body.error);
  settled.message = body.message;
  return { settled, body };
}

/**
 * FIN-1003: the tax authority is inside one of its published scheduled closures. Its message
 * ("Skatteverket is closed until 05:00 — try again later.") is the one tax-account failure a
 * user is shown verbatim, because it says when to come back.
 */
const TAX_AUTHORITY_CLOSED = "TAX_AUTHORITY_CLOSED";

/**
 * Reads a failed edge-function call's body once. Throws a CodedError carrying the server's
 * sentence when it is the TAX_AUTHORITY_CLOSED refusal; otherwise returns the parsed body, for
 * handleError's preParsedBody (a Response body can only be read once).
 */
export async function throwIfTaxAuthorityClosed(error: unknown): Promise<{ error?: string; message?: string } | null> {
  const body: { error?: string; message?: string; errorParams?: unknown } | null = await extractFunctionError(error);
  if (body?.error === TAX_AUTHORITY_CLOSED) {
    const closed = codedError(TAX_AUTHORITY_CLOSED);
    closed.message = body.message ?? "";
    closed.errorParams = sanitizeErrorParams(body.errorParams);
    throw closed;
  }
  return body;
}

/** The closure sentence, worded in the user's language, when `error` is the TAX_AUTHORITY_CLOSED refusal, else null. */
export function taxAuthorityClosedMessage(error: unknown): string | null {
  const coded = error as CodedError | null;
  if (coded?.error !== TAX_AUTHORITY_CLOSED) return null;
  return errorSentence(TAX_AUTHORITY_CLOSED, coded.errorParams) ?? (coded.message || null);
}

/**
 * Tax-account connects the server refuses for the company itself, which retrying never gets
 * past, so its sentence is shown instead of "Please try again", and none is a fault:
 * a legal form whose connect is not supported yet (FIN-1130), a sole trader's stored number that
 * cannot be resolved (FIN-1130, FIN-1552), a legal form with no tax profile (FIN-1167), and a
 * taxpayer another company already holds a live tax account for (FIN-1167 EC5).
 */
const TAX_CONNECT_NOT_ALLOWED: ReadonlySet<string> = new Set([
  "TAX_SOLE_TRADER_UNSUPPORTED",
  "TAX_SOLE_TRADER_NUMBER_UNVERIFIED",
  "TAX_SOLE_TRADER_SAMORDNINGSNUMMER",
  "TAX_LEGAL_FORM_UNKNOWN",
  "TAX_PRINCIPAL_ALREADY_CONNECTED",
]);

/**
 * Throws a CodedError carrying the server's code and sentence when an already-parsed failure body
 * is one of the connect refusals; otherwise does nothing.
 */
export function throwIfTaxConnectNotAllowed(body: { error?: string; message?: string; errorParams?: unknown } | null): void {
  if (!body?.error || !TAX_CONNECT_NOT_ALLOWED.has(body.error)) return;
  const refused = codedError(body.error);
  refused.message = body.message ?? "";
  refused.errorParams = sanitizeErrorParams(body.errorParams);
  throw refused;
}

/** The refusal sentence, worded in the user's language, when `error` is one of the connect refusals, else null. */
export function taxConnectNotAllowedMessage(error: unknown): string | null {
  const coded = error as CodedError | null;
  if (!coded?.error || !TAX_CONNECT_NOT_ALLOWED.has(coded.error)) return null;
  return errorSentence(coded.error, coded.errorParams) ?? (coded.message || null);
}

/**
 * FIN-1167 EC3: the company has booked tax-account entries, so a legal-form change waits for the
 * user to confirm it (backend errorType, 409). Not a fault: the settings form asks, then resends.
 */
const COMPANY_LEGAL_FORM_CHANGED_WITH_POSTINGS = "COMPANY_LEGAL_FORM_CHANGED_WITH_POSTINGS";

/** What the confirmation step shows about the held legal-form change. */
export interface LegalFormChangeConfirmation {
  fromLegalForm: string | null;
  toLegalForm: string | null;
  approvedTaxPostingCount: number;
}

/**
 * Throws a CodedError carrying the change's details when an already-parsed failure body is the
 * held legal-form change; otherwise does nothing.
 */
export function throwIfLegalFormChangeNeedsConfirmation(
  body: { error?: string; message?: string; details?: unknown } | null,
): void {
  if (body?.error !== COMPANY_LEGAL_FORM_CHANGED_WITH_POSTINGS) return;
  const held = codedError(COMPANY_LEGAL_FORM_CHANGED_WITH_POSTINGS, body.details);
  held.message = body.message ?? "";
  throw held;
}

/** The held change when `error` is the legal-form confirmation refusal, else null. */
export function legalFormChangeConfirmation(error: unknown): LegalFormChangeConfirmation | null {
  const coded = error as CodedError | null;
  if (coded?.error !== COMPANY_LEGAL_FORM_CHANGED_WITH_POSTINGS) return null;
  const details = (coded.details ?? {}) as Partial<LegalFormChangeConfirmation>;
  return {
    fromLegalForm: details.fromLegalForm ?? null,
    toLegalForm: details.toLegalForm ?? null,
    approvedTaxPostingCount: Number(details.approvedTaxPostingCount ?? 0),
  };
}

/** FIN-1045: the sales report's date has no fiscal year to book it in (backend errorType). */
export const SALES_REPORT_OUTSIDE_FISCAL_YEAR = "SALES_REPORT_OUTSIDE_FISCAL_YEAR";
/** FIN-1045: the report was marked received, but its bookkeeping job could not be queued. */
export const SUGGESTION_ENQUEUE_FAILED = "SUGGESTION_ENQUEUE_FAILED";

/** Mark-as-Received failures the server names, mapped to their toast title; the server's
 * sentence is shown as the description. */
const NAMED_MARK_AS_RECEIVED_FAILURES: Record<string, string> = {
  [SALES_REPORT_OUTSIDE_FISCAL_YEAR]: "No fiscal year for this report",
  [SUGGESTION_ENQUEUE_FAILED]: "Bookkeeping not started",
};

/** A CodedError carrying the server's sentence when a parsed edge-function body names one of
 * NAMED_MARK_AS_RECEIVED_FAILURES, else null. */
export function namedMarkAsReceivedFailure(body: { error?: string; message?: string } | null): CodedError | null {
  if (!body?.error || !body.message || !(body.error in NAMED_MARK_AS_RECEIVED_FAILURES)) return null;
  const failure = codedError(body.error);
  failure.message = body.message;
  return failure;
}

/** The toast for a named Mark-as-Received failure, else null. */
export function markAsReceivedFailure(error: unknown): { code: string; title: string; description: string } | null {
  const coded = error as CodedError | null;
  const title = coded?.error ? NAMED_MARK_AS_RECEIVED_FAILURES[coded.error] : undefined;
  return title ? { code: coded!.error, title, description: coded!.message } : null;
}

/** The server's refusal text when `error` is any NAMED_MATCH_REFUSALS code, else null. */
export function settledRefusalMessage(error: unknown): string | null {
  const coded = error as CodedError | null;
  return coded?.error && coded.error in NAMED_MATCH_REFUSALS ? coded.message : null;
}
